/**
 * Calculate matchpoint percentages for pairs
 */

/**
 * Calculate matchpoint percentage for a result
 * @param {number} MPs - Matchpoints scored
 * @param {number} maxMPs - Maximum possible matchpoints on this board
 * @returns {number} Percentage (0-100)
 */
export function calculateMatchpointPercentage(MPs, maxMPs) {
  if (maxMPs === 0) return 0;
  return (MPs / maxMPs) * 100;
}

/**
 * Calculate overall percentage for a pair across all boards
 * @param {number} totalMPs - Total matchpoints scored
 * @param {number} totalPossible - Total possible matchpoints
 * @returns {number} Percentage (0-100)
 */
export function calculateOverallPercentage(totalMPs, totalPossible) {
  if (totalPossible === 0) return 0;
  return (totalMPs / totalPossible) * 100;
}

/**
 * Recalculate matchpoint percentages from travellers
 * @param {Object} travellers - Traveller data
 * @returns {Object} Board number -> results with percentages
 */
export function calculateTravellerPercentages(travellers) {
  const result = {};
  
  Object.keys(travellers).forEach(boardNum => {
    const results = travellers[boardNum];
    const numResults = results.length;
    const maxMPs = (numResults - 1) * 2; // Each result can score 0 to 2*(n-1) MPs
    
    result[boardNum] = results.map(r => ({
      ...r,
      nsPercentage: calculateMatchpointPercentage(r.nsPts, maxMPs),
      ewPercentage: calculateMatchpointPercentage(r.ewPts, maxMPs),
    }));
  });
  
  return result;
}

/**
 * Recalculate pair scores from travellers
 * @param {Object} travellers - Traveller data
 * @returns {Array} Array of pair scores with correct percentages
 */
export function recalculatePairScores(travellers) {
  const pairTotals = {};
  let resultsPerBoard = 0;

  // Collect all MPs for each pair and count results
  Object.keys(travellers).forEach(boardNum => {
    const results = travellers[boardNum];
    resultsPerBoard = Math.max(resultsPerBoard, results.length);
    
    results.forEach(result => {
      // NS pair
      if (result.ns) {
        if (!pairTotals[result.ns]) {
          pairTotals[result.ns] = { pair: result.ns, totalMPs: 0, boardsPlayed: 0, direction: 'NS' };
        }
        pairTotals[result.ns].totalMPs += result.nsPts || 0;
        pairTotals[result.ns].boardsPlayed++;
      }
      
      // EW pair
      if (result.ew) {
        if (!pairTotals[result.ew]) {
          pairTotals[result.ew] = { pair: result.ew, totalMPs: 0, boardsPlayed: 0, direction: 'EW' };
        }
        pairTotals[result.ew].totalMPs += result.ewPts || 0;
        pairTotals[result.ew].boardsPlayed++;
      }
    });
  });
  
  // Calculate percentages
  // Each board has (resultsPerBoard - 1) comparisons, max 2 MPs per comparison
  const scores = Object.values(pairTotals).map(pair => {
    const maxPerBoard = (resultsPerBoard - 1) * 2;
    const maxPossible = pair.boardsPlayed * maxPerBoard;
    const percentage = calculateOverallPercentage(pair.totalMPs, maxPossible);
    
    return {
      pair: pair.pair,
      totalMPs: pair.totalMPs,
      maxPossible,
      percentage,
      boardsPlayed: pair.boardsPlayed,
    };
  });
  
  // Sort by percentage
  scores.sort((a, b) => b.percentage - a.percentage);
  
  // Assign positions
  let position = 1;
  scores.forEach((score, idx) => {
    if (idx > 0 && Math.abs(score.percentage - scores[idx - 1].percentage) > 0.01) {
      position = idx + 1;
    }
    score.position = position;
  });
  
  return scores;
}
